import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, MapPinIcon, CheckCircle2Icon } from 'lucide-react';
import { DeckResult } from '../../types/deck';
const EASE = [0.23, 1, 0.32, 1] as const;
interface TalabResultCardProps {
  result: DeckResult;
  delay?: number;
  /** Mirrors the real card's selected state (used when the user picks an item). */
  selected?: boolean;
  width?: number;
  /**
   * Draws the eye to an AI-suggested item with ONE brand glow sweep, then
   * settles. Used for the cross-sell card so the audience registers that
   * Talab added it — without leaving a distracting looping pulse on screen.
   */
  highlight?: boolean;
  /** Right-to-left content (Arabic product names). */
  rtl?: boolean;
}

/**
 * Compact restaging of components/ListingCard.tsx so results inside the deck's
 * Talab window read as the actual product, not a deck illustration.
 */
export function TalabResultCard({
  result,
  delay = 0,
  selected = false,
  width = 172,
  highlight = false,
  rtl = false
}: TalabResultCardProps) {
  return <motion.div className="relative shrink-0" style={{
    width
  }} dir={rtl ? 'rtl' : undefined} initial={{
    opacity: 0,
    y: 14,
    scale: 0.97
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.4,
    ease: EASE,
    delay
  }} data-id="element-2326" data-mp="5ugrl9zzw5y1">
      {highlight && <motion.span className="absolute -inset-[5px] rounded-[20px] bg-gradient-to-r from-primary-indigo to-ai-purple blur-lg" aria-hidden="true" initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: [0, 0.55, 0],
      scale: [0.97, 1.03, 1.01]
    }} transition={{
      duration: 1.9,
      times: [0, 0.32, 1],
      ease: EASE,
      delay: delay + 0.2
    }} data-id="element-2327" data-mp="atjxewnsug72" />}
      <motion.div className={`relative bg-white rounded-2xl overflow-hidden border shadow-soft flex flex-col h-full ${selected ? 'border-primary-indigo ring-2 ring-primary-indigo/30' : ''}`} animate={highlight ? {
      borderColor: ['rgba(241,245,249,1)', 'rgba(168,85,247,0.75)', 'rgba(241,245,249,1)']
    } : {
      borderColor: selected ? 'rgba(67,56,202,1)' : 'rgba(241,245,249,1)'
    }} transition={highlight ? {
      duration: 1.9,
      times: [0, 0.32, 1],
      ease: EASE,
      delay: delay + 0.2
    } : {
      duration: 0.3,
      ease: EASE
    }} data-id="element-2328" data-mp="18efv3xvd2ko3">
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100" data-id="element-2329" data-mp="1yb03sefagj5q">
        <img src={result.image} alt={result.title} className="w-full h-full object-cover" data-id="element-2330" data-mp="238jh9c45wrwm" />
        <div className={`absolute top-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 backdrop-blur-sm ${rtl ? 'right-2' : 'left-2'}`} data-id="element-2331" data-mp="285rzfcpb8vxv">
          <SparklesIcon className="w-2.5 h-2.5 text-ai-purple" data-id="element-2332" data-mp="dtv7zjqvbq2t" />
          <span className="text-[8.5px] font-semibold text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-2333" data-mp="6a2p8y6s2bbc">
            {result.match}
          </span>
        </div>
        {result.availability && <div className={`absolute bottom-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-success text-white ${rtl ? 'right-2' : 'left-2'}`} data-id="element-2334" data-mp="3spx7eup76y8x">
            <CheckCircle2Icon className="w-2.5 h-2.5" data-id="element-2335" data-mp="p73xqszjitk1" />
            <span className="text-[8.5px] font-medium" data-id="element-2336" data-mp="k9lkwrieju6r">{result.availability}</span>
          </div>}
      </div>

      <div className="flex flex-col flex-1 p-2.5" data-id="element-2337" data-mp="f9g11xidjbz8">
        <div className="flex items-start justify-between gap-2" data-id="element-2338" data-mp="fcbzro21q4oq">
          <h4 className="font-display font-bold text-[11.5px] text-dark-navy leading-tight line-clamp-1" data-id="element-2339" data-mp="aacug8mqwxp5">
            {result.title}
          </h4>
          <p className="font-bold text-[11.5px] text-primary-indigo whitespace-nowrap" data-id="element-2340" data-mp="2zy3txb0iwqvf">
            {result.price}
            {result.priceNote && <span className="text-[8.5px] font-medium text-slate-400" data-id="element-2341" data-mp="npeqpwjpjc3a">{result.priceNote}</span>}
          </p>
        </div>

        <div className="flex items-center gap-1 mt-1 text-slate-400" data-id="element-2342" data-mp="siq0o6jtg8zf">
          {result.color ? <span className="w-2.5 h-2.5 rounded-full border border-slate-200 shrink-0" style={{
            backgroundColor: result.color
          }} aria-hidden="true" data-id="element-2343" data-mp="dq5yp98ek7jk" /> : <MapPinIcon className="w-2.5 h-2.5" data-id="element-2344" data-mp="1bfhei0gl9vw3" />}
          <span className="text-[9px] line-clamp-1" data-id="element-2345" data-mp="1bezkzblb9vrv">{result.meta}</span>
        </div>

        {result.specs && <div className="grid grid-cols-2 gap-1 mt-2" data-id="element-2346" data-mp="wkuqeu758lgs">
            {result.specs.map(spec => <div key={spec.label} className="bg-slate-50 rounded-md px-1.5 py-1" data-id="element-2347" data-mp="11jeh17womlfh">
                <p className="text-[7px] uppercase tracking-wide text-slate-400" data-id="element-2348" data-mp="3hatx70cgcoht">{spec.label}</p>
                <p className="text-[9px] font-semibold text-dark-navy" data-id="element-2349" data-mp="3m93q14apfd09">{spec.value}</p>
              </div>)}
          </div>}

        <div className="flex items-center gap-1.5 mt-auto pt-2.5" data-id="element-2350" data-mp="1zbm78zc565hx">
          <button type="button" className={`flex-1 h-6 rounded-lg text-[9px] font-semibold text-white ${selected ? 'bg-dark-navy' : 'bg-gradient-to-r from-primary-indigo to-ai-purple'}`} data-id="element-2351" data-mp="1zdgq883urnjd">
            {selected ? rtl ? 'أُضيف' : 'Added' : result.primaryCta ?? (rtl ? 'اطلب الآن' : 'Book Now')}
          </button>
          <button type="button" className="h-6 px-2 rounded-lg border border-slate-200 text-[9px] font-medium text-slate-500" data-id="element-2352" data-mp="1udklg6nbzs2r">
            {rtl ? 'التفاصيل' : 'Details'}
          </button>
        </div>
      </div>
      </motion.div>
    </motion.div>;
}